import React, { createContext, useState, useContext, useCallback } from 'react';
import api from '../services/api';
import { API_ENDPOINTS, REVIEW_STATUS } from '../utils/constants';
import { storage } from '../utils/helpers';

const ReviewContext = createContext();

export const ReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const isDemoMode = useCallback(() => {
    return storage.get('demo_mode') === true;
  }, []);

  const fetchReviews = useCallback(async (locationId) => {
    setLoading(true);
    try {
      const endpoint = isDemoMode() ? API_ENDPOINTS.DEMO.REVIEWS : API_ENDPOINTS.REVIEWS.LIST;
      const response = await api.get(endpoint, {
        params: isDemoMode() ? {} : { locationId }
      });
      const reviewsData = response.data.reviews || [];
      setReviews(reviewsData.map(r => ({ 
        ...r, 
        id: r.id || r.googleReviewId,
        status: r.status || REVIEW_STATUS.PENDING 
      })));
      return { reviews: reviewsData, usage: response.data.usage };
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isDemoMode]);

  const generateReply = async (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review) return { success: false, error: 'Review not found' };

    try {
      const endpoint = isDemoMode() ? API_ENDPOINTS.DEMO.GENERATE_REPLY : API_ENDPOINTS.REVIEWS.GENERATE_REPLY;
      const response = await api.post(endpoint, {
        reviewId,
        reviewText: review.comment,
        rating: review.starRating,
        reviewerName: review.reviewer?.displayName || 'Customer'
      });
      
      const reply = response.data.reply;
      
      setReviews(prevReviews => prevReviews.map(r => 
        r.id === reviewId 
          ? { ...r, generatedReply: reply, status: REVIEW_STATUS.REPLY_GENERATED }
          : r
      ));
      return { success: true, reply, usage: response.data.usage };
    } catch (error) {
      console.error('Error generating reply:', error);
      // Check if it's a limit reached error
      if (error.response?.status === 403 && error.response?.data?.limitReached) {
        return { success: false, limitReached: true, error: 'Free plan limit reached' };
      }
      return { success: false, error: error.response?.data?.message || error.message };
    }
  };

  const postReply = async (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review) return { success: false, error: 'Review not found' };

    try {
      const endpoint = isDemoMode() ? API_ENDPOINTS.DEMO.POST_REPLY : API_ENDPOINTS.REVIEWS.POST_REPLY;
      await api.post(endpoint, {
        reviewId,
        replyText: review.generatedReply
      });
      
      setReviews(prevReviews => prevReviews.map(r => 
        r.id === reviewId ? { ...r, status: REVIEW_STATUS.POSTED } : r
      ));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  };

  const updateReply = (reviewId, newReply) => {
    setReviews(prevReviews => prevReviews.map(r => 
      r.id === reviewId 
        ? { ...r, generatedReply: newReply, status: REVIEW_STATUS.REPLY_GENERATED }
        : r
    ));
  };

  const value = {
    reviews,
    loading,
    fetchReviews,
    generateReply,
    postReply,
    updateReply
  };

  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
};

export const useReviews = () => {
  const context = useContext(ReviewContext);
  if (!context) throw new Error('useReviews must be used within ReviewProvider');
  return context;
};

export default ReviewContext;
